import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FileText, Calendar, CheckCircle, HelpCircle, Download, ArrowRight } from 'lucide-react';
import PageHeader from '../../components/common/PageHeader';

const Admissions = () => {
  const [activeTab, setActiveTab] = useState('montessori');

  const admissionSteps = [
    { icon: FileText, title: 'Submit Application', description: 'Complete the online application form with all required documents.' },
    { icon: Calendar, title: 'Schedule Assessment', description: 'Book an assessment date for your child at your preferred campus.' },
    { icon: CheckCircle, title: 'Admission Decision', description: 'Receive your admission decision within 5-7 working days.' },
  ];

  const requirements = {
    montessori: [
      'Birth certificate (original and copy)',
      'Passport photographs (4 copies)',
      'Immunization records',
      'Previous school report (if applicable)',
      'Parent/Guardian ID',
    ],
    college: [
      'Birth certificate (original and copy)',
      'Passport photographs (4 copies)',
      'Previous school reports (last 2 years)',
      'Transfer certificate (if applicable)',
      'Parent/Guardian ID',
      'Recommendation letter from previous school',
    ],
  };

  const faqs = [
    {
      question: 'What is the admission timeline?',
      answer: 'Admissions are open year-round, but we recommend applying early. The main intake is in September, with a secondary intake in January.',
    },
    {
      question: 'Is there an entrance examination?',
      answer: 'Yes, all prospective students undergo an age-appropriate assessment to determine their readiness and appropriate class placement.',
    },
    {
      question: 'What are the school fees?',
      answer: 'Please contact our admissions office for the current fee structure. We offer flexible payment plans to suit different family needs.',
    },
    {
      question: 'Do you offer scholarships?',
      answer: 'Yes, we offer merit-based scholarships and financial aid to deserving students. Contact admissions for more information.',
    },
  ];

  return (
    <>
      <PageHeader
        title="Admissions"
        subtitle="Begin your child's journey to excellence"
        breadcrumbs={[{ label: 'Admissions' }]}
        backgroundImage="https://images.unsplash.com/photo-1427504494785-3a9ca7044f45?w=1600&h=400&fit=crop"
      />

      {/* Admission Process */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="inline-block px-4 py-1 bg-primary-100 text-primary-600 rounded-full text-sm font-medium mb-4">
              How to Apply
            </span>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-gray-900 mb-4">
              Admission Process
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Our straightforward admission process ensures a smooth experience for families.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {admissionSteps.map((step, index) => {
              const Icon = step.icon;
              return (
                <motion.div
                  key={step.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="relative text-center p-6"
                >
                  <div className="w-16 h-16 bg-primary-600 rounded-full flex items-center justify-center mx-auto mb-4 text-white">
                    <Icon size={28} />
                  </div>
                  <div className="absolute top-8 left-[60%] w-[80%] h-0.5 bg-primary-200 hidden md:block last:hidden" />
                  <span className="text-sm text-primary-600 font-medium">Step {index + 1}</span>
                  <h3 className="text-xl font-bold text-gray-900 mt-2 mb-2">{step.title}</h3>
                  <p className="text-gray-600">{step.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Requirements */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold text-gray-900 mb-4">
              Admission Requirements
            </h2>
          </motion.div>

          {/* Tabs */}
          <div className="flex justify-center mb-8">
            <div className="inline-flex bg-white rounded-lg p-1 shadow-soft">
              <button
                onClick={() => setActiveTab('montessori')}
                className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                  activeTab === 'montessori'
                    ? 'bg-primary-600 text-white'
                    : 'text-gray-600 hover:text-primary-600'
                }`}
              >
                Montessori & Primary
              </button>
              <button
                onClick={() => setActiveTab('college')}
                className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                  activeTab === 'college'
                    ? 'bg-primary-600 text-white'
                    : 'text-gray-600 hover:text-primary-600'
                }`}
              >
                College
              </button>
            </div>
          </div>

          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto bg-white rounded-2xl p-8 md:p-10 shadow-soft"
          >
            <h3 className="text-xl font-bold text-gray-900 mb-6">Required Documents</h3>
            <div className="grid md:grid-cols-2 gap-4">
              {requirements[activeTab].map((req, index) => (
                <div key={index} className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl">
                  <CheckCircle className="text-primary-600 flex-shrink-0 mt-0.5" size={20} />
                  <span className="text-gray-700">{req}</span>
                </div>
              ))}
            </div>

            <div className="mt-8 pt-6 border-t border-gray-100 flex flex-col sm:flex-row gap-4">
              <button className="btn-primary">
                <Download className="mr-2" size={20} />
                Download Application Form
              </button>
              <button className="btn-secondary">
                Apply Online
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQs */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="inline-block px-4 py-1 bg-primary-100 text-primary-600 rounded-full text-sm font-medium mb-4">
              FAQs
            </span>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
          </motion.div>

          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-gray-50 rounded-xl p-6"
              >
                <div className="flex items-start gap-4">
                  <HelpCircle className="text-primary-600 flex-shrink-0 mt-1" size={20} />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">{faq.question}</h3>
                    <p className="text-gray-600">{faq.answer}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-primary-600">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-white mb-4">
            Ready to Apply?
          </h2>
          <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
            Take the first step towards giving your child an exceptional education.
          </p>
          <button className="inline-flex items-center justify-center px-8 py-4 bg-white text-primary-600 font-semibold rounded-lg hover:bg-primary-50 transition-colors group">
            Start Application
            <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
          </button>
        </div>
      </section>
    </>
  );
};

export default Admissions;
